import { DateRange } from '../components/DateRangeInput/DateRangeInput';

/** A generic function for filtering an array. */
export type FilterFunction<T> = (items: T[]) => T[];

/**
 * Create a filter for `item.productId`.
 *
 * @param productIds A list of product IDs to include. If an empty set of IDs
 * is passed, no items will be filtered out.
 * @returns A filter function that filters items by product.
 */
export function createProductIdFilter<
  T extends {
    productId: string;
  },
>(productIds?: string[] | Set<string>): FilterFunction<T> {
  const productIdSet = productIds instanceof Set ? productIds : new Set(productIds);
  return (items: T[]) =>
    productIdSet.size === 0 ? items : items.filter((item) => productIdSet.has(item.productId));
}

/**
 * Create a filter for `item.createdAt` and `item.updatedAt`.
 *
 * @param dateRange DateRange object with from and optional to dates.
 * @param checkUpdated Whether to also check updatedAt, defaults to true. Items
 * only need one of `createdAt` or `updatedAt` to be in the range.
 * @returns A filter function that filters items by date range.
 */
export function createDateRangeFilter<
  T extends {
    createdAt: number;
    updatedAt?: number;
  },
>(dateRange?: DateRange, checkUpdated: boolean = true): (items: T[]) => T[] {
  if (!dateRange?.from) {
    return (items) => items;
  }

  const fromTimestamp = dateRange.from.getTime();
  const toTimestamp = dateRange.to?.getTime() ?? new Date().getTime();

  return (items: T[]) => {
    return items.filter((item) => {
      // Check if createdAt is within range
      const createdInRange = item.createdAt >= fromTimestamp && item.createdAt <= toTimestamp;

      // If we're not checking updatedAt or if the item doesn't have updatedAt, return createdInRange
      if (!checkUpdated || !item.updatedAt) {
        return createdInRange;
      }

      // Check if updatedAt is within range
      const updatedInRange = item.updatedAt >= fromTimestamp && item.updatedAt <= toTimestamp;

      // Item matches if either created or updated timestamps are in range
      return createdInRange || updatedInRange;
    });
  };
}
